import { useCallback, useEffect, useMemo, useState } from 'react';
import { monthlyHoursFor, todayIsoParis, type Team } from '@lemontaigu/staff';
import { ADMIN_API_BASE } from './config.js';
import { supabase } from './supabase.js';
import { useRegister, useRegisterWrite } from '@lemontaigu/staff/react';
import { FrDateInput } from '@lemontaigu/reception/ui/FrDateInput.js';
import {
  brutHoraireFromMensuel,
  closeContractError,
  closeContractParams,
  contractDeletable,
  contractDraftError,
  contractMutable,
  contractParams,
  contractRange,
  contractTerminated,
  contractToDraft,
  emptyContractDraft,
  emptyPersonDraft,
  fmtDateShort,
  filterEmployees,
  personDraftError,
  personParams,
  personToDraft,
  rankByHireOrder,
  registerMaxHireRank,
  sortEmployees,
  teamLabel,
  TEAMS,
  usedMotifs,
  type AdminEmployee,
  type ContractDraft,
  type ContractPeriod,
  type PersonDraft,
  type SortKey,
  type StatusFilter,
  type TeamFilter,
} from './logic.js';

const TODAY = todayIsoParis();

// « Registre » (operator-only) — the personnel register (RUP source of truth,
// migrations 0023–0025): every person present & past, with full contract
// history. Master/slave like the menu editor. Rehiring a returning CDD = open
// their card → « Réembaucher (mêmes conditions) » clones the last contract's
// terms into a new period. All writes go through is_admin() SECURITY DEFINER RPCs.

const CONTRACT_TYPES = ['CDI', 'CDD', 'Saisonnier', 'Extra', 'Apprentissage', 'Stage', 'Autre'];

/** Newest-first copy of a person's periods (for the panel list + « latest »). */
const byRecent = (cs: ContractPeriod[]): ContractPeriod[] =>
  [...cs].sort((a, b) => (a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : 0));

export function Registre() {
  const register = useRegister();
  const [rows, setRows] = useState<AdminEmployee[] | null>(null);
  const [err, setErr] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null); // employee id, 'new', or null
  const [newSeed, setNewSeed] = useState<ContractDraft | null>(null); // « Dupliquer » → seed a new person

  // Defaults (operator, 2026-07-17): the working view is the CURRENT team by name.
  const [status, setStatus] = useState<StatusFilter>('current');
  const [team, setTeam] = useState<TeamFilter>('all');
  // « Trier par équipe » is the default (operator, 2026-08-06): the list is
  // read team by team far more often than alphabetically.
  const [sort, setSort] = useState<SortKey>('team');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      setRows(await register.listEmployees());
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [register]);

  useEffect(() => {
    void load();
  }, [load]);

  const all = rows ?? [];
  const rankById = useMemo(() => rankByHireOrder(all), [all]);
  const visible = useMemo(
    () => sortEmployees(filterEmployees(all, { status, team, q: search }), sort),
    [all, status, team, search, sort],
  );

  const selected = selectedId && selectedId !== 'new' ? all.find((e) => e.id === selectedId) ?? null : null;

  return (
    <div className="reg">
      <div className="reg-toolbar no-print">
        <div className="reg-filters">
          <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="all">Tous</option>
            <option value="current">En poste</option>
            <option value="past">Sortis</option>
          </select>
          <select value={team} onChange={(e) => setTeam(e.target.value as TeamFilter)}>
            <option value="all">Toutes les équipes</option>
            {TEAMS.map((t) => (
              <option key={t} value={t}>
                {teamLabel(t)}
              </option>
            ))}
            <option value="none">Sans équipe</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="team">Trier par équipe</option>
            <option value="name">Trier par nom</option>
            <option value="rank">Trier par rang</option>
            <option value="end">Trier par fin de contrat</option>
          </select>
          <input
            type="search"
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          type="button"
          className="auth-btn"
          onClick={() => {
            setNewSeed(null);
            setSelectedId('new');
          }}
        >
          + Nouvelle personne
        </button>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {rows === null && <p className="staff-empty">Chargement…</p>}

      {rows !== null && (
        <div className="reg-count">
          {visible.length} personne{visible.length === 1 ? '' : 's'}
        </div>
      )}

      <div className="reg-table-wrap">
        <table className="reg-table">
          <thead>
            <tr>
              <th className="reg-c-rank">Rang</th>
              <th>Nom</th>
              <th>Équipe</th>
              <th>Statut</th>
              <th>Contrat en cours</th>
              <th>PIN</th>
              <th>Compte</th>
            </tr>
          </thead>
          <tbody>
            {rows !== null && visible.length === 0 && (
              <tr>
                <td colSpan={7} className="reg-empty">
                  Aucune personne ne correspond.
                </td>
              </tr>
            )}
            {visible.map((e) => (
              <tr
                key={e.id}
                className={(selectedId === e.id ? 'is-selected ' : '') + (e.isCurrent ? '' : 'is-past')}
                onClick={() => setSelectedId(e.id)}
              >
                <td className="reg-c-rank">{rankById.get(e.id) || '—'}</td>
                <td className="reg-c-name">
                  <span className="reg-c-title">
                    {e.firstName} {e.lastName}
                    {e.nickname && <span className="reg-nickname">« {e.nickname} »</span>}
                    {e.isSenior && <span className="emp-badge">senior</span>}
                  </span>
                  <span className="reg-c-sub">ID Paie {e.employeeId}</span>
                </td>
                <td>
                  {e.team ? (
                    <span className={`team-chip team-${e.team}`}>{teamLabel(e.team)}</span>
                  ) : (
                    <span className="reg-muted">—</span>
                  )}
                </td>
                <td>
                  <span className={`reg-status ${e.isCurrent ? 'is-current' : 'is-past'}`}>
                    {e.isCurrent ? 'En poste' : 'Ancien·ne'}
                  </span>
                </td>
                <td>
                  {e.isCurrent && e.contractStartDate
                    ? contractRange({ startDate: e.contractStartDate, endDate: e.contractEndDate })
                    : e.contracts.length
                      ? `fin ${fmtDateShort(byRecent(e.contracts)[0]?.endDate ?? null)}`
                      : <span className="reg-muted">aucun contrat</span>}
                </td>
                <td>{e.hasPin ? <span className="emp-ok">●</span> : <span className="emp-warn">○</span>}</td>
                <td>{e.isLinked ? <span className="emp-ok">●</span> : <span className="reg-muted">○</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(selected || selectedId === 'new') && (
        <PersonPanel
          key={selected?.id ?? (newSeed ? 'new-dup' : 'new')}
          person={selected}
          allEmployees={all}
          initialSeed={selectedId === 'new' ? newSeed : null}
          onClose={() => {
            setNewSeed(null);
            setSelectedId(null);
          }}
          onSaved={(id) => {
            void load();
            if (id) setSelectedId(id);
          }}
          onDuplicate={(seed) => {
            setNewSeed(seed);
            setSelectedId('new');
          }}
        />
      )}
    </div>
  );
}

function PersonPanel({
  person,
  allEmployees,
  initialSeed,
  onClose,
  onSaved,
  onDuplicate,
}: {
  person: AdminEmployee | null;
  allEmployees: AdminEmployee[];
  initialSeed?: ContractDraft | null;
  onClose: () => void;
  onSaved: (id?: string) => void;
  onDuplicate: (seed: ContractDraft) => void;
}) {
  const isNew = person === null;
  const registerWrite = useRegisterWrite();
  const [draft, setDraft] = useState<PersonDraft>(person ? personToDraft(person) : emptyPersonDraft());
  const [initialContract, setInitialContract] = useState<ContractDraft>(initialSeed ?? emptyContractDraft());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = <K extends keyof PersonDraft>(k: K, v: PersonDraft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setIc = <K extends keyof ContractDraft>(k: K, v: ContractDraft[K]) =>
    setInitialContract((c) => ({ ...c, [k]: v }));

  // Flat list of every existing contract → the « copy an existing contract's
  // terms » picker for a new hire.
  const contractOptions = useMemo(
    () =>
      allEmployees.flatMap((e) =>
        e.contracts.map((c) => ({
          id: c.id,
          contract: c,
          label: `${e.firstName} ${e.lastName} — ${c.emploi ?? c.contractType ?? 'contrat'} (${c.startDate.slice(0, 4)})`,
        })),
      ),
    [allEmployees],
  );

  const copyTermsFrom = (contractId: string) => {
    const src = contractOptions.find((o) => o.id === contractId)?.contract;
    if (src) setInitialContract(contractToDraft(src, true));
  };

  const save = async () => {
    const vErr = personDraftError(draft);
    if (vErr) return setErr(vErr);
    const hasInitial = isNew && initialContract.startDate.trim() !== '';
    if (hasInitial) {
      const cErr = contractDraftError(initialContract);
      if (cErr) return setErr(cErr);
    }
    setBusy(true);
    setErr('');
    let newId: string;
    try {
      if (hasInitial) {
        // hasInitial implies isNew (person === null) — see its definition above.
        // New hire: person + first contract, atomically (migration 0056, F15).
        // The RPC assigns the contract to the person it just created — the
        // employeeId in contractParams is ignored server-side, so a placeholder
        // is fine here.
        newId = await registerWrite.createHire(
          personParams(draft, null),
          contractParams(initialContract, '', null),
        );
      } else {
        newId = await registerWrite.savePerson(personParams(draft, person?.id ?? null));
        if (hasInitial) await registerWrite.addContract(contractParams(initialContract, newId, null));
      }
    } catch (e) {
      setBusy(false);
      return setErr(e instanceof Error ? e.message : String(e));
    }
    setBusy(false);
    onSaved(newId);
  };

  return (
    <div className="reg-detail">
      <div className="reg-detail-head">
        <h2 className="section-title">
          {isNew
            ? 'Nouvelle personne'
            : `${person?.firstName} ${person?.lastName}${person?.nickname ? ` « ${person.nickname} »` : ''}`}
        </h2>
        <button type="button" className="reg-close" onClick={onClose}>
          Fermer
        </button>
      </div>

      <div className="emp-form-grid">
        <Field label="ID Paie">
          <input value={draft.employeeId} onChange={(e) => set('employeeId', e.target.value)} />
        </Field>
        <Field label="Prénom">
          <input value={draft.firstName} onChange={(e) => set('firstName', e.target.value)} />
        </Field>
        <Field label="Nom">
          <input value={draft.lastName} onChange={(e) => set('lastName', e.target.value)} />
        </Field>
        <Field label="Surnom (reconnaissance — messages, Planning, Pointage)">
          <input value={draft.nickname} onChange={(e) => set('nickname', e.target.value)} />
        </Field>
        <Field label="Nom de naissance">
          <input value={draft.birthName} onChange={(e) => set('birthName', e.target.value)} />
        </Field>
        <Field label="Sexe">
          <select value={draft.sex} onChange={(e) => set('sex', e.target.value as '' | 'F' | 'M')}>
            <option value="">—</option>
            <option value="F">F</option>
            <option value="M">M</option>
          </select>
        </Field>
        <Field label="Date de naissance">
          <FrDateInput value={draft.birthDate} onChange={(v) => set('birthDate', v)} />
        </Field>
        <Field label="Nationalité">
          <input value={draft.nationality} onChange={(e) => set('nationality', e.target.value)} />
        </Field>
        <Field label="N° Sécurité Sociale">
          <input value={draft.ssn} onChange={(e) => set('ssn', e.target.value)} />
        </Field>
        <Field label="Adresse" wide>
          <input value={draft.address} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <Field label="E-mail (identifiant de connexion — facultatif)">
          <input type="email" value={draft.email} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Téléphone (+33… — facultatif)">
          <input value={draft.phone} onChange={(e) => set('phone', e.target.value)} />
        </Field>
        <Field label="Équipe">
          <select value={draft.team} onChange={(e) => set('team', e.target.value as '' | Team)}>
            <option value="">— non défini —</option>
            {TEAMS.map((t) => (
              <option key={t} value={t}>
                {teamLabel(t)}
              </option>
            ))}
          </select>
        </Field>
        <label className="emp-check">
          <input type="checkbox" checked={draft.isSenior} onChange={(e) => set('isSenior', e.target.checked)} />
          <span>Senior (référent·e d’équipe)</span>
        </label>
      </div>

      {isNew && (
        <fieldset className="reg-initial">
          <legend>Contrat initial (facultatif)</legend>
          {initialSeed && (
            <p className="reg-clone-note">Conditions dupliquées — renseignez l’identité et ajustez les dates.</p>
          )}
          {contractOptions.length > 0 && (
            <label className="ts-field reg-field-wide">
              <span>Copier les conditions d’un contrat existant</span>
              <select defaultValue="" onChange={(e) => e.target.value && copyTermsFrom(e.target.value)}>
                <option value="">— saisie manuelle —</option>
                {contractOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <ContractFields draft={initialContract} onField={setIc} />
        </fieldset>
      )}

      {err && <p className="auth-err">{err}</p>}

      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void save()}>
          {busy ? '…' : isNew ? 'Créer la fiche' : 'Enregistrer la fiche'}
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={onClose}>
          Annuler
        </button>
      </div>

      {person && (
        <>
          <ContractsSection
            person={person}
            allEmployees={allEmployees}
            onChanged={() => onSaved(person.id)}
            onDuplicate={onDuplicate}
          />
          <PinSetter employeeId={person.id} firstName={person.firstName} onChanged={() => onSaved(person.id)} />
          <AccessGranter person={person} onChanged={() => onSaved(person.id)} />
        </>
      )}
    </div>
  );
}

function Field({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <label className={`ts-field${wide ? ' reg-field-wide' : ''}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function ContractsSection({
  person,
  allEmployees,
  onChanged,
  onDuplicate,
}: {
  person: AdminEmployee;
  allEmployees: AdminEmployee[];
  onChanged: () => void;
  onDuplicate: (seed: ContractDraft) => void;
}) {
  // 'new' | 'close:<id>' | a contract id (edit) | null
  const [editing, setEditing] = useState<string | null>(null);
  const [seed, setSeed] = useState<ContractDraft | null>(null);
  const periods = byRecent(person.contracts);
  const registerMaxRank = useMemo(() => registerMaxHireRank(allEmployees), [allEmployees]);
  const motifOptions = useMemo(() => usedMotifs(allEmployees), [allEmployees]);

  const openNew = (s: ContractDraft | null) => {
    setSeed(s);
    setEditing('new');
  };

  return (
    <section className="reg-contracts">
      <div className="reg-contracts-head">
        <h3>Contrats</h3>
        {editing !== 'new' && (
          <button type="button" onClick={() => openNew(null)}>
            + Ajouter
          </button>
        )}
      </div>

      {editing === 'new' && (
        <ContractForm
          employeeId={person.id}
          seed={seed}
          onDone={() => {
            setEditing(null);
            onChanged();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      {person.contracts.length === 0 && editing !== 'new' && (
        <p className="staff-empty">Aucun contrat. Ajoutez une période pour mettre la personne « en poste ».</p>
      )}

      <ul className="reg-contract-list">
        {periods.map((c) => {
          const mutable = contractMutable(c, person.contracts, TODAY);
          const deletable = contractDeletable(c, registerMaxRank);
          const closable = mutable && c.endDate != null; // needs an end date on file before "clôturer" means anything
          if (editing === c.id) {
            return (
              <li key={c.id}>
                <ContractEditForm
                  contract={c}
                  mutable={mutable}
                  onDone={() => {
                    setEditing(null);
                    onChanged();
                  }}
                  onCancel={() => setEditing(null)}
                />
              </li>
            );
          }
          if (editing === `close:${c.id}`) {
            return (
              <li key={c.id}>
                <CloseContractDialog
                  contract={c}
                  motifOptions={motifOptions}
                  onDone={() => {
                    setEditing(null);
                    onChanged();
                  }}
                  onCancel={() => setEditing(null)}
                />
              </li>
            );
          }
          return (
            <li key={c.id} className="reg-contract-row">
              <span className="reg-contract-rank">{c.hireRank == null ? '—' : `n°${c.hireRank}`}</span>
              <span className="reg-contract-type">{c.contractType ?? 'Contrat'}</span>
              <span className="reg-contract-range">{contractRange(c)}</span>
              {c.emploi && <span className="reg-contract-emploi">{c.emploi}</span>}
              {contractTerminated(c) && (
                <span className="reg-contract-closed" title={c.motifSortie ?? ''}>
                  Clôturé le {fmtDateShort(c.closedAt)}
                </span>
              )}
              <span className="reg-contract-actions">
                <button type="button" onClick={() => setEditing(c.id)}>
                  Modifier
                </button>
                {closable && (
                  <button type="button" onClick={() => setEditing(`close:${c.id}`)}>
                    Clôturer
                  </button>
                )}
                <button
                  type="button"
                  title="Nouvelle période pour cette personne, mêmes conditions"
                  onClick={() => openNew(contractToDraft(c, true))}
                >
                  ↻ Réembaucher
                </button>
                <button
                  type="button"
                  title="Nouvelle personne avec ces conditions de contrat"
                  onClick={() => onDuplicate(contractToDraft(c, true))}
                >
                  Dupliquer
                </button>
                {deletable && <DeleteContractButton contractId={c.id} onDone={onChanged} />}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * Restricted editor for an existing contract (register integrity, 0027): the
 * term fields are frozen at creation; only « Note » is always editable, and
 * end date / motif de sortie only while the contract is mutable (last hire +
 * current) and not yet terminated.
 */
function ContractEditForm({
  contract,
  mutable,
  onDone,
  onCancel,
}: {
  contract: ContractPeriod;
  mutable: boolean;
  onDone: () => void;
  onCancel: () => void;
}) {
  // Full draft — same fields as the creation form (operator request
  // 2026-07-19: see the whole record, not a terse 4-field summary). Only
  // `editableKeys` below actually persist; the rest render read-only —
  // register-integrity (0027) still freezes the substantive terms.
  const registerWrite = useRegisterWrite();
  const [draft, setDraft] = useState<ContractDraft>(contractToDraft(contract));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onField = <K extends keyof ContractDraft>(k: K, v: ContractDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const terminated = contractTerminated(contract);
  // `mutable` already excludes a closed contract (staff_contract_mutable /
  // contractMutable both check closedAt), so this is just `mutable` — kept as
  // its own name for readability at the call site below.
  const closeEditable = mutable;
  const editableKeys = useMemo(() => {
    const keys: (keyof ContractDraft)[] = ['note', 'contractNumber'];
    if (closeEditable) keys.push('endDate', 'motifSortie');
    return new Set(keys);
  }, [closeEditable]);

  const save = async () => {
    if (draft.endDate !== '' && draft.endDate < contract.startDate) return setErr('La fin précède le début.');
    setBusy(true);
    setErr('');
    try {
      await registerWrite.saveContract({
        p_id: contract.id,
        p_end_date: draft.endDate === '' ? null : draft.endDate,
        p_motif_sortie: draft.motifSortie === '' ? null : draft.motifSortie,
        p_note: draft.note === '' ? null : draft.note,
        p_contract_number: draft.contractNumber.trim() === '' ? null : draft.contractNumber.trim(),
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reg-contract-form">
      {contract.changeDate && <p className="reg-muted">modifié le {fmtDateShort(contract.changeDate)}</p>}
      {!closeEditable && (
        <p className="reg-lock-note">
          {terminated
            ? 'Contrat clôturé — seuls la note et le N° de contrat restent modifiables.'
            : 'Contrat figé (ce n’est pas la dernière embauche encore en cours) — seuls la note et le N° de contrat restent modifiables.'}
        </p>
      )}

      <ContractFields draft={draft} onField={onField} editableKeys={editableKeys} />

      {err && <p className="auth-err">{err}</p>}
      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void save()}>
          {busy ? '…' : 'Enregistrer'}
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * The explicit "clôturer" act (migration 20260811) — separate from the
 * regular contract editor. Sets closedAt + motifSortie atomically via
 * staff_admin_close_contract and, unlike the editor, is a ONE-WAY door: once
 * confirmed there is no UI path back (register-integrity intent, 0027/0040).
 * `Motif de sortie` is a combobox (datalist) over every motif already used in
 * the register, but free entry is always still allowed.
 */
function CloseContractDialog({
  contract,
  motifOptions,
  onDone,
  onCancel,
}: {
  contract: ContractPeriod;
  motifOptions: string[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const registerWrite = useRegisterWrite();
  const [closedAt, setClosedAt] = useState(TODAY);
  const [motifSortie, setMotifSortie] = useState(contract.motifSortie ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const confirm = async () => {
    const vErr = closeContractError(contract, closedAt, motifSortie);
    if (vErr) return setErr(vErr);
    setBusy(true);
    setErr('');
    try {
      await registerWrite.closeContract(closeContractParams(contract.id, closedAt, motifSortie));
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reg-contract-form reg-close-form">
      <p className="reg-lock-note">
        Clôturer verrouille définitivement ce contrat (fin le {fmtDateShort(contract.endDate)}) — aucune réouverture
        depuis l’écran ensuite.
      </p>
      <div className="emp-form-grid">
        <Field label="Date de clôture effective">
          <FrDateInput value={closedAt} onChange={setClosedAt} />
        </Field>
        <Field label="Motif de sortie" wide>
          <input
            value={motifSortie}
            list="reg-motif-options"
            onChange={(e) => setMotifSortie(e.target.value)}
            placeholder="Fin de CDD, démission, licenciement…"
          />
          <datalist id="reg-motif-options">
            {motifOptions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </Field>
      </div>
      {err && <p className="auth-err">{err}</p>}
      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void confirm()}>
          {busy ? '…' : 'Clôturer'}
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}

function ContractForm({
  employeeId,
  contract,
  seed,
  onDone,
  onCancel,
}: {
  employeeId: string;
  contract?: ContractPeriod;
  seed?: ContractDraft | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const registerWrite = useRegisterWrite();
  const [draft, setDraft] = useState<ContractDraft>(
    contract ? contractToDraft(contract) : seed ?? emptyContractDraft(),
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const onField = <K extends keyof ContractDraft>(k: K, v: ContractDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    const vErr = contractDraftError(draft);
    if (vErr) return setErr(vErr);
    setBusy(true);
    setErr('');
    try {
      await registerWrite.addContract(contractParams(draft, employeeId, contract?.id ?? null));
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="reg-contract-form">
      {seed && !contract && <p className="reg-clone-note">Conditions copiées — ajustez les dates et le rang.</p>}
      <ContractFields draft={draft} onField={onField} />
      {err && <p className="auth-err">{err}</p>}
      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy} onClick={() => void save()}>
          {busy ? '…' : 'Enregistrer le contrat'}
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * Full contract field grid — shared by the creation form (everything
 * editable, `editableKeys` omitted) and the edit form for an existing
 * contract (register integrity, 0027/0040: only `editableKeys` stay live —
 * the rest render greyed-out so the operator can still SEE the full record,
 * just not change frozen terms through this screen).
 */
function ContractFields({
  draft,
  onField,
  editableKeys,
}: {
  draft: ContractDraft;
  onField: <K extends keyof ContractDraft>(k: K, v: ContractDraft[K]) => void;
  editableKeys?: Set<keyof ContractDraft>;
}) {
  const isEditable = (k: keyof ContractDraft) => editableKeys === undefined || editableKeys.has(k);
  const txt = (k: keyof ContractDraft, label: string, wide = false) => (
    <Field label={label} wide={wide}>
      <input value={draft[k] as string} disabled={!isEditable(k)} onChange={(e) => onField(k, e.target.value as never)} />
    </Field>
  );
  return (
    <>
      <div className="emp-form-grid">
        <Field label="Type">
          <select
            value={draft.contractType}
            disabled={!isEditable('contractType')}
            onChange={(e) => onField('contractType', e.target.value)}
          >
            {!CONTRACT_TYPES.includes(draft.contractType) && draft.contractType !== '' && (
              <option value={draft.contractType}>{draft.contractType}</option>
            )}
            {CONTRACT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Numéro de contrat (DSN)">
          <input
            value={draft.contractNumber}
            disabled={!isEditable('contractNumber')}
            onChange={(e) => onField('contractNumber', e.target.value)}
          />
        </Field>
        <Field label="Début">
          <FrDateInput
            value={draft.startDate}
            disabled={!isEditable('startDate')}
            onChange={(v) => onField('startDate', v)}
          />
        </Field>
        <Field label="Fin (vide = en cours / CDI)">
          <FrDateInput
            value={draft.endDate}
            disabled={!isEditable('endDate')}
            onChange={(v) => {
              onField('endDate', v);
              if (v === '') onField('motifSortie', ''); // motif only for closed contracts
            }}
          />
        </Field>
        {txt('emploi', 'Emploi')}
        {txt('qualification', 'Qualification')}
        {txt('pcsEse', 'PCS-ESE')}
        {txt('statutConventionnel', 'Statut conventionnel')}
        {txt('niveau', 'Niveau')}
        {txt('echelon', 'Échelon')}
        {txt('coef', 'Coefficient')}
        {txt('brutMensuel', 'Brut mensuel')}
        <Field label="Brut horaire">
          <div className="reg-inline">
            <input
              value={draft.brutHoraire}
              disabled={!isEditable('brutHoraire')}
              onChange={(e) => onField('brutHoraire', e.target.value)}
            />
            {isEditable('brutHoraire') && (
              <button
                type="button"
                className="reg-compute"
                title="Calculer depuis le brut mensuel (base 151,67 h)"
                onClick={() => {
                  const h = brutHoraireFromMensuel(Number(draft.brutMensuel.replace(',', '.')));
                  if (h != null) onField('brutHoraire', h.toFixed(2));
                }}
              >
                ↺ mensuel
              </button>
            )}
          </div>
        </Field>
        {/* Contractual VOLUME — the hourly rate above is what an hour is worth,
            this is how many are owed. The staffing allocator needs it to avoid
            piling work on someone already over their hours (brief G2); leaving
            it blank simply means that person has no hours signal, which is not
            the same as being far below target. */}
        <Field label="Heures hebdomadaires">
          <div className="reg-inline">
            <input
              value={draft.weeklyHours}
              disabled={!isEditable('weeklyHours')}
              placeholder="35"
              onChange={(e) => onField('weeklyHours', e.target.value)}
            />
            <span className="equipe-hint">
              {draft.weeklyHours.trim() === ''
                ? 'vide = inconnu'
                : `${monthlyHoursFor(Number(draft.weeklyHours.replace(',', '.'))) || '—'} h/mois`}
            </span>
          </div>
        </Field>
        {(draft.endDate !== '' || !isEditable('motifSortie')) && txt('motifSortie', 'Motif de sortie')}
        <label className="emp-check">
          <input
            type="checkbox"
            checked={draft.tempsPartiel}
            disabled={!isEditable('tempsPartiel')}
            onChange={(e) => onField('tempsPartiel', e.target.checked)}
          />
          <span>Temps partiel</span>
        </label>
        {txt('note', 'Note', true)}
      </div>

      <details className="reg-more">
        <summary>Mentions spéciales (travailleurs étrangers / jeunes travailleurs)</summary>
        <div className="emp-form-grid">
          <Field label="Autorisation — entrée">
            <FrDateInput
              value={draft.authEntree}
              disabled={!isEditable('authEntree')}
              onChange={(v) => onField('authEntree', v)}
            />
          </Field>
          <Field label="Autorisation — sortie">
            <FrDateInput
              value={draft.authSortie}
              disabled={!isEditable('authSortie')}
              onChange={(v) => onField('authSortie', v)}
            />
          </Field>
          {txt('docType', 'Type de document')}
          {txt('docNumber', 'N° d’ordre du document')}
          <label className="emp-check">
            <input
              type="checkbox"
              checked={draft.apprenti}
              disabled={!isEditable('apprenti')}
              onChange={(e) => onField('apprenti', e.target.checked)}
            />
            <span>Apprenti·e</span>
          </label>
          <label className="emp-check">
            <input
              type="checkbox"
              checked={draft.contratQualification}
              disabled={!isEditable('contratQualification')}
              onChange={(e) => onField('contratQualification', e.target.checked)}
            />
            <span>Contrat de qualification</span>
          </label>
          <label className="emp-check">
            <input
              type="checkbox"
              checked={draft.contratAdaptation}
              disabled={!isEditable('contratAdaptation')}
              onChange={(e) => onField('contratAdaptation', e.target.checked)}
            />
            <span>Contrat d’adaptation</span>
          </label>
        </div>
      </details>
    </>
  );
}

function DeleteContractButton({ contractId, onDone }: { contractId: string; onDone: () => void }) {
  const registerWrite = useRegisterWrite();
  const [busy, setBusy] = useState(false);
  const del = async () => {
    if (!window.confirm('Supprimer cette période de contrat ?')) return;
    setBusy(true);
    try {
      await registerWrite.deleteContract(contractId);
      onDone();
    } catch (e) {
      window.alert(`Erreur : ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="reg-del" disabled={busy} onClick={() => void del()}>
      Supprimer
    </button>
  );
}

function PinSetter({
  employeeId,
  firstName,
  onChanged,
}: {
  employeeId: string;
  firstName: string;
  onChanged: () => void;
}) {
  const registerWrite = useRegisterWrite();
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const save = async () => {
    setBusy(true);
    setMsg('');
    try {
      await registerWrite.setPin(employeeId, pin.trim());
      setPin('');
      setOpen(false);
      onChanged();
    } catch (e) {
      setMsg(`Erreur : ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="reg-pin-row">
        <button type="button" onClick={() => setOpen(true)}>
          Définir / réinitialiser le code PIN
        </button>
      </div>
    );
  }
  return (
    <div className="emp-pin">
      <label className="ts-field">
        <span>Code PIN de {firstName} (4 à 8 chiffres)</span>
        <input
          type="password"
          inputMode="numeric"
          pattern="\d{4,8}"
          maxLength={8}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
        />
      </label>
      {msg && <p className="auth-err">{msg}</p>}
      <div className="cand-actions">
        <button type="button" className="auth-btn" disabled={busy || !/^\d{4,8}$/.test(pin)} onClick={() => void save()}>
          Enregistrer le PIN
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={() => setOpen(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}

/**
 * « Créer l'accès » (RUNBOOK §18.17): public sign-ups are disabled, so a new
 * employee's first OTP attempt fails until an auth account exists for their
 * e-mail. This calls the admin-only grant-access edge function — one click,
 * same effect as the service-role script it replaces.
 */
function AccessGranter({ person, onChanged }: { person: AdminEmployee; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  if (!person.email) {
    return <p className="reg-muted">Aucun e-mail sur la fiche — impossible de créer un accès.</p>;
  }
  if (person.isLinked) {
    return (
      <>
        <p className="staff-empty">Compte staff.* déjà lié à {person.email}.</p>
        <AccessRevoker person={person} onChanged={onChanged} />
      </>
    );
  }

  const grant = async () => {
    setBusy(true);
    setMsg('');
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('session expirée');
      const res = await fetch(`${ADMIN_API_BASE}/api/admin/grant-access`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({ employeeId: person.id }),
      });
      const payload = (await res.json().catch(() => null)) as { error?: string; status?: string } | null;
      if (!res.ok) throw new Error(payload?.error ?? `échec (${res.status})`);
      setBusy(false);
      onChanged();
    } catch (err) {
      setBusy(false);
      setMsg(err instanceof Error ? err.message : 'échec');
    }
  };

  return (
    <div className="reg-pin-row">
      <button type="button" disabled={busy} onClick={() => void grant()}>
        {busy ? '…' : `Créer l’accès staff.* (${person.email})`}
      </button>
      {msg && <p className="auth-err">{msg}</p>}
    </div>
  );
}

/**
 * « Retirer tous les accès » (brief §A, ADR-0023). One atomic RPC: bans the
 * login, ends sessions, removes the staff role, per-user grants and push
 * subscriptions, unlinks the fiche, logs it. It does NOT close the contract —
 * that is the legal act on the RUP and can differ in time (préavis, last day),
 * so the confirmation says so rather than leaving the operator to wonder.
 */
function AccessRevoker({ person, onChanged }: { person: AdminEmployee; onChanged: () => void }) {
  const registerWrite = useRegisterWrite();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const revoke = async () => {
    setBusy(true);
    setMsg('');
    try {
      await registerWrite.revokeAccess(person.id, reason.trim() === '' ? null : reason.trim());
    } catch (err) {
      setBusy(false);
      setMsg(err instanceof Error ? err.message : 'échec');
      return;
    }
    setBusy(false);
    setOpen(false);
    onChanged();
  };

  if (!open) {
    return (
      <div className="reg-pin-row">
        <button type="button" className="ts-danger" onClick={() => setOpen(true)}>
          Retirer tous les accès
        </button>
      </div>
    );
  }
  return (
    <div className="reg-pin-row reg-revoke">
      <p className="ts-warning">
        {person.firstName} {person.lastName} ne pourra plus se connecter à aucune application (staff.*, bureau,
        bos.*). Ses sessions en cours sont coupées. Sa fiche, ses contrats et ses pointages sont conservés. Le
        contrat n’est <strong>pas</strong> clôturé — c’est un acte distinct (« Clôturer »).
      </p>
      <label className="ts-field">
        <span>Motif (optionnel)</span>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      {msg && <p className="auth-err">{msg}</p>}
      <div className="cand-actions">
        <button type="button" className="ts-danger" disabled={busy} onClick={() => void revoke()}>
          {busy ? '…' : 'Confirmer le retrait des accès'}
        </button>
        <button type="button" className="cand-dismiss" disabled={busy} onClick={() => setOpen(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}
